import React, { useEffect, useState } from 'react';

import { Box } from '@chakra-ui/react';
import useAsync from 'components/hooks/useAsync';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Loader } from 'screens/analytics/commonComponents';
import { GoalAnalyticsProvider } from 'screens/analytics/goals/GoalAnalyticsContext';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import {
  checkFeedbackModuleEnabled,
  checkGoalModuleEnabled,
  checkLevelUpModuleEnabled,
} from 'utils/helpers/moduleAccess';

import { getMatchPathIndexFromRoutesArray } from 'constants/helper';
import { routes } from 'constants/routes';

import { getCompetencySettings } from 'services/competencies';
import { getPerformancePreview } from 'services/performanceReview';

import { ExpandableContainerProvider } from 'uiToolkitV1/ExpandableContainerProvider';
import { PageTabbedNavigation } from 'uiToolkitV1/PageTabbedNavigation';
import { PaperWithShadow } from 'uiToolkitV1/Paper';

import PreviewPerformanceGrid from '../components/PreviewPerformanceGrid/PreviewPerformanceGrid';
import ReviewCompetencyTable from './ReviewCompetencyTable';
import { ReviewGoalsTable } from './ReviewGoalsTable';

// todo: vikalp add any logic for permissions and access
const DEFAULT_LANDING_PAGE_TABS = [
  {
    name: 'Explore',
    route: routes.performanceReview.audit,
    accessFn: () => true,
  },
];

const PREVIEW_PERFORMANCE_TAB = {
  name: 'Preview Performance',
  route: routes.performanceReview.performancePreview,
  accessFn: () => true,
};

const ReviewPerformancePreview = () => {
  const location = useLocation();
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const [gridData, setGridData] = useState([]);
  const [lineChartData, setLineChartData] = useState([]);
  const [showPerformanceGridChart, setShowPerformanceGridChart] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCompetencyEnable, setIsCompetencyEnable] = useState(null);
  const rows = gridData?.length || 0;
  const columns = gridData[0]?.length || 0;
  const routesArray = [...DEFAULT_LANDING_PAGE_TABS, PREVIEW_PERFORMANCE_TAB].map((tabData) => tabData.route);
  const [includePreviewPerformanceTab, setIncludePreviewPerformanceTab] = useState(false);
  const { run, isSuccess } = useAsync();
  const history = useHistory();
  const isHorizontal1DGraph = rows === 1;

  useEffect(() => {
    run(
      getCompetencySettings().then(({ viewType }) => {
        const isCompetencyModuleEnable = viewType !== 'DISABLED';

        const isIncludePerformancePreviewTab = !(
          (viewType === 'DISABLED' || (!checkFeedbackModuleEnabled() && !checkLevelUpModuleEnabled())) &&
          !checkGoalModuleEnabled()
        );

        setIsCompetencyEnable(isCompetencyModuleEnable);
        setIncludePreviewPerformanceTab(isIncludePerformancePreviewTab);
      })
    );
  }, [run]);

  let selectedTabRouteIndex = getMatchPathIndexFromRoutesArray({
    pathname: location.pathname,
    routesArray,
  });
  // only failure case where the user does not have preview performance tab access but the url tab query params is for preview performance
  // just reset to first tab
  if (isSuccess && selectedTabRouteIndex === 1 && !includePreviewPerformanceTab) {
    selectedTabRouteIndex = 0;
    history.push(routes.performanceReview.audit);
  }
  const selectedTabName = [...DEFAULT_LANDING_PAGE_TABS, PREVIEW_PERFORMANCE_TAB]?.[selectedTabRouteIndex]?.name ?? '';

  const getPerformancePreviewGrid = async () => {
    setIsLoading(true);
    try {
      const res = await getPerformancePreview({
        empId: loggedInEmployee.id,
        type: 'rabbit',
      });
      setGridData(res.chartData);
      setLineChartData((prevLineChartData) => ({
        ...prevLineChartData,
        ratingMap: res.ratingMap,
        reviewRatingDetailsResponseList: res.reviewRatingDetailsResponseList,
        dataMap: res.dataMap,
        expandedViewRatingDetails: res.expandedViewRatingDetails,
      }));
      setShowPerformanceGridChart(res.isShowPerformancePreviewGraph);
    } catch (error) {
      console.log('error occurs while fetching performance preview graph: ', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getPerformancePreviewGrid();
  }, [loggedInEmployee]);

  const visibleTabs = includePreviewPerformanceTab
    ? [...DEFAULT_LANDING_PAGE_TABS, PREVIEW_PERFORMANCE_TAB]
    : DEFAULT_LANDING_PAGE_TABS;

  return (
    <Box>
      <PageTabbedNavigation tabsData={visibleTabs} selectedTabName={selectedTabName} title="My Reviews" />
      <Box p={showPerformanceGridChart ? 4 : 2}>
        {isLoading ? (
          <Loader />
        ) : (
          checkGoalModuleEnabled() &&
          isCompetencyEnable &&
          (checkFeedbackModuleEnabled() || checkLevelUpModuleEnabled()) &&
          showPerformanceGridChart && (
            <PaperWithShadow w="100%" py={0} minHeight="361px">
              <ExpandableContainerProvider>
                <PreviewPerformanceGrid
                  lineChartData={lineChartData}
                  gridData={gridData}
                  rows={rows}
                  columns={columns}
                  hideYAxisLabelTicks={isHorizontal1DGraph}
                />
              </ExpandableContainerProvider>
            </PaperWithShadow>
          )
        )}
      </Box>
      {checkGoalModuleEnabled() && (
        <GoalAnalyticsProvider>
          <ReviewGoalsTable />
        </GoalAnalyticsProvider>
      )}
      <ReviewCompetencyTable source="ReviewPerformancePreview" isCompetencyEnable={isCompetencyEnable} />
    </Box>
  );
};

export default ReviewPerformancePreview;
